import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import config from '../config/siteConfig.json';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const { iniciales, headerColor, navegacion } = config;
  const [mostrarHorarios, setMostrarHorarios] = useState(false);
  const [textosConfig, setTextosConfig] = useState(null);
  const [direccionesConfig, setDireccionesConfig] = useState(null);
  const [mesasConfig, setMesasConfig] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  // v72.3 — sesión web (para mostrar botón "Cerrar sesión")
  const [haySesion, setHaySesion] = useState(false);

  useEffect(() => {
    const check = () => {
      try { setHaySesion(sessionStorage.getItem('webAuthenticated') === 'true'); }
      catch (e) { setHaySesion(false); }
    };
    check();
    window.addEventListener('webauth:updated', check);
    window.addEventListener('storage', check);
    return () => {
      window.removeEventListener('webauth:updated', check);
      window.removeEventListener('storage', check);
    };
  }, []);

  const cerrarSesion = () => {
    if (!window.confirm('¿Cerrar sesión? Volverás a la pantalla de acceso.')) return;
    try {
      sessionStorage.removeItem('webAuthenticated');
      sessionStorage.removeItem('webAuthInvitado');
      sessionStorage.removeItem('webAuthToken');
      sessionStorage.removeItem('webAuthVia');
    } catch (e) {}
    window.location.href = '/';
  };

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [eventoRes, textosRes, direccionesRes, mesasRes] = await Promise.all([
          fetch(`${API_URL}/api/evento/config`),
          fetch(`${API_URL}/api/public/config/textos`),
          fetch(`${API_URL}/api/public/direcciones`),
          fetch(`${API_URL}/api/public/mesas`),
        ]);
        
        if (eventoRes.ok) {
          const data = await eventoRes.json();
          // Mostrar enlace a Horarios si está publicado y el botón Menú está activado
          setMostrarHorarios(
            data.horarios?.publicar_seccion && data.horarios?.publicar_menu
          );
        }
        
        if (textosRes.ok) {
          const data = await textosRes.json();
          setTextosConfig(data);
        }
        
        if (direccionesRes.ok) {
          const data = await direccionesRes.json();
          setDireccionesConfig(data);
        }

        if (mesasRes.ok) {
          const data = await mesasRes.json();
          setMesasConfig(data);
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Obtener valores de iniciales (dinámicos o estáticos)
  const getInicialesNovia = () => textosConfig?.inicial_novia || iniciales.novia;
  const getInicialesNovio = () => textosConfig?.inicial_novio || iniciales.novio;
  // Si el conector está vacío, devolver espacio no-rompible para mantener la distancia
  const getConector = () => {
    const conector = textosConfig?.conector;
    if (conector === '' || conector === undefined) {
      return '\u00A0'; // Non-breaking space
    }
    return conector || iniciales.textoUnion;
  };
  // Orden de los novios (aplica también al header)
  const getInicialPrimaria = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero' ? getInicialesNovio() : getInicialesNovia();
  };
  const getInicialSecundaria = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero' ? getInicialesNovia() : getInicialesNovio();
  };
  const getNombrePrimarioH = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero'
      ? (textosConfig?.nombre_novio || 'Novio')
      : (textosConfig?.nombre_novia || 'Novia');
  };
  const getNombreSecundarioH = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero'
      ? (textosConfig?.nombre_novia || 'Novia')
      : (textosConfig?.nombre_novio || 'Novio');
  };
  
  // Determinar el tipo de header: iniciales, nombres, imagen, nada
  const getHeaderTipo = () => textosConfig?.header_tipo || 'iniciales';
  
  // Obtener estilos de iniciales
  const getInicialesEstilo = () => {
    if (textosConfig?.iniciales_estilo) {
      const e = textosConfig.iniciales_estilo;
      const sizePx = isMobile && e.tamano_movil ? e.tamano_movil : (e.tamano || 24);
      return {
        fontFamily: e.fuente || iniciales.fuente,
        fontSize: `${sizePx}px`,
        color: iniciales.color,
        fontWeight: e.negrita ? 'bold' : (iniciales.negrita === 'si' ? 'bold' : 'normal'),
        fontStyle: e.cursiva ? 'italic' : 'normal'
      };
    }
    return {
      fontFamily: iniciales.fuente,
      fontSize: '48px',
      color: iniciales.color,
      fontWeight: iniciales.negrita === 'si' ? 'bold' : 'normal'
    };
  };

  // Estilo del texto cuando el header muestra NOMBRES (independiente de las iniciales)
  const getHeaderNombresEstilo = () => {
    const e = textosConfig?.header_nombres_estilo;
    const sizePx = e
      ? (isMobile && e.tamano_movil ? e.tamano_movil : (e.tamano || 24))
      : 24;
    return {
      fontFamily: e?.fuente || iniciales.fuente,
      fontSize: `${sizePx}px`,
      color: iniciales.color,
      fontWeight: e?.negrita ? 'bold' : 'normal',
      fontStyle: e?.cursiva ? 'italic' : 'normal',
    };
  };

  // Altura del logo del header en px según viewport
  const getHeaderLogoAltura = () => {
    const desktop = textosConfig?.header_logo_altura ?? 48;
    const mobile = textosConfig?.header_logo_altura_movil;
    const v = isMobile && (mobile != null) ? mobile : desktop;
    return Math.max(16, Math.min(160, Number(v) || 48));
  };

  // Renderizar el logo según el tipo
  const renderHeaderLogo = () => {
    const headerTipo = getHeaderTipo();
    const conector = getConector();
    
    if (headerTipo === 'nada') {
      return null;
    }
    
    if (headerTipo === 'imagen' && textosConfig?.header_imagen_url) {
      const altura = getHeaderLogoAltura();
      return (
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <img loading="lazy" decoding="async" 
            src={`${API_URL}${textosConfig.header_imagen_url}`} 
            alt="Logo" 
            className="object-contain"
            style={{ height: `${altura}px`, maxWidth: '320px' }}
          />
        </Link>
      );
    }
    
    if (headerTipo === 'nombres') {
      return (
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <span style={getHeaderNombresEstilo()}>
            {getNombrePrimarioH()} {conector} {getNombreSecundarioH()}
          </span>
        </Link>
      );
    }
    
    // Por defecto: iniciales
    return (
      <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
        <span style={getInicialesEstilo()}>
          {getInicialPrimaria()}
        </span>
        <span style={getInicialesEstilo()}>
          {conector}
        </span>
        <span style={getInicialesEstilo()}>
          {getInicialSecundaria()}
        </span>
      </Link>
    );
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 border-b"
      style={{ 
        backgroundColor: headerColor,
        borderColor: '#E8DCC4',
        ...(textosConfig?.header_altura_modo === 'fija' ? {
          height: `${(isMobile && (textosConfig.header_altura_movil != null) ? textosConfig.header_altura_movil : (textosConfig.header_altura ?? 96))}px`,
        } : {}),
      }}
    >
      <div 
        className={
          textosConfig?.header_altura_modo === 'fija'
            ? "container mx-auto h-full flex items-center justify-between"
            : "container mx-auto py-6 flex items-center justify-between"
        }
        style={{
          paddingLeft: `${(isMobile && (textosConfig?.header_padding_left_movil != null) ? textosConfig.header_padding_left_movil : (textosConfig?.header_padding_left ?? 16))}px`,
          paddingRight: '16px',
        }}
      >
        {/* Logo del header según configuración */}
        {renderHeaderLogo()}

        {/* Navegación */}
        <nav className="hidden md:flex items-center gap-8">
          <Link 
            to="/" 
            className="text-base hover:text-[#D4846D] transition-colors font-medium"
            style={{ color: '#5C5C5C', fontSize: '19px' }}
          >
            {navegacion.inicio}
          </Link>
          {(textosConfig?.mostrar_confirmar_menu !== false) && (
            <Link 
              to="/confirmacion" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
              data-testid="menu-confirmar"
            >
              {navegacion.confirmar}
            </Link>
          )}
          {mostrarHorarios && (
            <Link 
              to="/horarios" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
            >
              Horarios
            </Link>
          )}
          {direccionesConfig?.mostrar !== false && (
            <Link 
              to="/direcciones" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
            >
              {direccionesConfig?.nombre_menu || 'Direcciones'}
            </Link>
          )}
          {mesasConfig?.mostrar_en_menu && (
            <Link 
              to="/donde-me-siento" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
              data-testid="link-donde-me-siento"
            >
              {mesasConfig?.nombre_menu || '¿Dónde me siento?'}
            </Link>
          )}
          {(textosConfig?.mostrar_galeria_menu !== false) && (
            <Link 
              to="/galeria" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
              data-testid="menu-galeria"
            >
              {navegacion.galeria}
            </Link>
          )}
          {(textosConfig?.mostrar_mensajes_menu !== false) && (
            <Link 
              to="/mensajes" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
              data-testid="menu-mensajes"
            >
              {navegacion.mensajes}
            </Link>
          )}
          {(textosConfig?.mostrar_concurso_menu !== false) && (
            <Link 
              to="/concurso" 
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px' }}
              data-testid="menu-concurso"
            >
              {navegacion.concurso}
            </Link>
          )}
          <Link 
            to="/contacto" 
            className="text-base hover:text-[#D4846D] transition-colors font-medium"
            style={{ color: '#5C5C5C', fontSize: '19px' }}
          >
            {navegacion.contacto}
          </Link>
          {haySesion && (
            <button
              type="button"
              onClick={cerrarSesion}
              className="text-base hover:text-[#D4846D] transition-colors font-medium"
              style={{ color: '#5C5C5C', fontSize: '19px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              data-testid="menu-cerrar-sesion"
              title="Cerrar sesión"
            >
              ⎋ Cerrar sesión
            </button>
          )}
        </nav>

        {/* Menú móvil (hamburguesa) */}
        <button 
          className="md:hidden p-2"
          onClick={() => {
            const menu = document.getElementById('mobile-menu');
            menu.classList.toggle('hidden');
          }}
        >
          <svg className="w-8 h-8" fill="none" stroke="#5C5C5C" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Menú móvil desplegable */}
      <div 
        id="mobile-menu" 
        className="hidden md:hidden border-t"
        style={{ 
          backgroundColor: headerColor,
          borderColor: '#E8DCC4'
        }}
      >
        <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
          <Link 
            to="/" 
            className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
            style={{ color: '#5C5C5C', fontSize: '18px' }}
            onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
          >
            {navegacion.inicio}
          </Link>
          {(textosConfig?.mostrar_confirmar_menu !== false) && (
            <Link 
              to="/confirmacion" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
              data-testid="menu-mobile-confirmar"
            >
              {navegacion.confirmar}
            </Link>
          )}
          {mostrarHorarios && (
            <Link 
              to="/horarios" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
            >
              Horarios
            </Link>
          )}
          {direccionesConfig?.mostrar !== false && (
            <Link 
              to="/direcciones" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
            >
              {direccionesConfig?.nombre_menu || 'Direcciones'}
            </Link>
          )}
          {mesasConfig?.mostrar_en_menu && (
            <Link 
              to="/donde-me-siento" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
            >
              {mesasConfig?.nombre_menu || '¿Dónde me siento?'}
            </Link>
          )}
          {(textosConfig?.mostrar_galeria_menu !== false) && (
            <Link 
              to="/galeria" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
              data-testid="menu-mobile-galeria"
            >
              {navegacion.galeria}
            </Link>
          )}
          {(textosConfig?.mostrar_mensajes_menu !== false) && (
            <Link 
              to="/mensajes" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
              data-testid="menu-mobile-mensajes"
            >
              {navegacion.mensajes}
            </Link>
          )}
          {(textosConfig?.mostrar_concurso_menu !== false) && (
            <Link 
              to="/concurso" 
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
              style={{ color: '#5C5C5C', fontSize: '18px' }}
              onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
              data-testid="menu-mobile-concurso"
            >
              {navegacion.concurso}
            </Link>
          )}
          <Link 
            to="/contacto" 
            className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium"
            style={{ color: '#5C5C5C', fontSize: '18px' }}
            onClick={() => document.getElementById('mobile-menu').classList.add('hidden')}
          >
            {navegacion.contacto}
          </Link>
          {haySesion && (
            <button
              type="button"
              onClick={() => { document.getElementById('mobile-menu').classList.add('hidden'); cerrarSesion(); }}
              className="text-base hover:text-[#D4846D] transition-colors py-2 font-medium text-left"
              style={{ color: '#5C5C5C', fontSize: '18px', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0' }}
              data-testid="menu-mobile-cerrar-sesion"
            >
              ⎋ Cerrar sesión
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
